import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './CyberLunaWindow.module.css';

// Global z-index counter — clicking any window brings it above the rest
let zCounter = 1000;

/**
 * CyberLunaWindow
 *
 * XP "Luna" window chrome recoloured with The Boombox dark palette.
 * Draggable via the title bar. Stacks correctly across multiple instances.
 *
 * Static windows (isStatic) get live title-bar controls:
 *   • Minimize — rolls the body up to the title bar (window-shade); click to expand.
 *   • Close (✕) — collapses to a small taskbar-style chip; click the chip to reopen.
 *   • Maximize (□) — opens the content in a full-screen modal (Esc / backdrop to close).
 * Draggable windows keep the classic callback behaviour (onClose / onMinimize / onMaximize).
 *
 * @param {string}          title
 * @param {React.ReactNode} children
 * @param {() => void}      onClose
 * @param {() => void}      [onMinimize]
 * @param {() => void}      [onMaximize]
 * @param {{ x: number, y: number }} [defaultPosition]
 * @param {number}          [minWidth=400]
 * @param {string}          [className]
 * @param {string}          [bodyClassName] - Extra class for the window body (in-flow and maximized)
 * @param {string}          [maxBodyClassName] - Extra class applied to the body only while maximized
 */
const CyberLunaWindow = ({
  title = 'Window',
  children,
  onClose,
  onMinimize,
  onMaximize,
  defaultPosition,
  minWidth = 400,
  className = '',
  bodyClassName = '',
  maxBodyClassName = '',
  isStatic = false,
}) => {
  // Centre on first render if no explicit position given (draggable mode only)
  const initPos = defaultPosition ?? {
    x: typeof window !== 'undefined' ? Math.max(0, (window.innerWidth - minWidth) / 2) : 100,
    y: typeof window !== 'undefined' ? Math.max(0, window.innerHeight * 0.15) : 80,
  };

  const [position, setPosition] = useState(initPos);
  const [zIndex, setZIndex] = useState(() => ++zCounter);

  // Use refs for drag state to avoid stale closures in the global listeners
  const posRef = useRef(initPos);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // ── Interactive window-state (static windows only) ──
  const [isShaded, setIsShaded] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Only static windows drive the built-in behaviour; draggable windows keep
  // their explicit parent callbacks so existing usage is undisturbed.
  const interactive = isStatic;

  const bringToFront = useCallback(() => {
    if (!isStatic) setZIndex(++zCounter);
  }, [isStatic]);

  const handleTitleBarMouseDown = useCallback((e) => {
    if (isStatic) return;
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - posRef.current.x,
      y: e.clientY - posRef.current.y,
    };
    e.preventDefault(); // prevent text selection while dragging
  }, [isStatic]);

  useEffect(() => {
    if (isStatic) return;
    const onMove = (e) => {
      if (!isDragging.current) return;
      const next = {
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      };
      posRef.current = next;
      setPosition(next);
    };
    const onUp = () => { isDragging.current = false; };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isStatic]);

  // ── Control handlers — run the built-in behaviour, then any parent callback ──
  const handleMinimize = useCallback((e) => {
    e?.stopPropagation();
    if (interactive) setIsShaded((v) => !v);
    onMinimize?.();
  }, [interactive, onMinimize]);

  const handleMaximize = useCallback((e) => {
    e?.stopPropagation();
    if (interactive) setIsMaximized(true);
    onMaximize?.();
  }, [interactive, onMaximize]);

  const handleClose = useCallback((e) => {
    e?.stopPropagation();
    if (interactive) setIsClosed(true);
    onClose?.();
  }, [interactive, onClose]);

  const handleRestore = useCallback(() => {
    setIsClosed(false);
    setIsShaded(false);
  }, []);

  const exitMaximized = useCallback(() => setIsMaximized(false), []);

  // Esc-to-close + body scroll-lock while the maximized modal is open
  useEffect(() => {
    if (!isMaximized) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsMaximized(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isMaximized]);

  // ── Closed → collapse to a restore chip ──
  if (isClosed) {
    return (
      <button
        type="button"
        className={`${styles.restoreChip} ${className}`}
        onClick={handleRestore}
        title={`Reopen ${title}`}
      >
        <span className={styles.restoreChipIcon} aria-hidden="true">◈</span>
        <span className={styles.restoreChipLabel}>{title}</span>
      </button>
    );
  }

  const windowStyle = isStatic
    ? {}
    : { left: position.x, top: position.y, zIndex, minWidth };

  // Content lives in exactly one place at a time — in the modal while maximized,
  // in-flow otherwise — so form fields / DOM ids are never duplicated.
  const inFlowBody = isMaximized ? (
    <p className={styles.maximizedNote}>Maximized — press Esc to restore.</p>
  ) : (
    children
  );

  return (
    <>
      <div
        className={`${styles.window} ${isStatic ? styles.staticWindow : ''} ${className}`}
        style={windowStyle}
        onMouseDown={isStatic ? undefined : bringToFront}
      >
        {/* ── Title Bar ── */}
        <div
          className={`${styles.titleBar} ${isStatic ? styles.staticTitleBar : ''}`}
          onMouseDown={handleTitleBarMouseDown}
        >
          {/* Window icon placeholder — keeps symmetry */}
          <span className={styles.windowIcon} aria-hidden="true">◈</span>

          <span className={styles.titleText}>{title}</span>

          <div className={styles.controls}>
            <button
              className={`${styles.controlBtn} ${styles.minimizeBtn}`}
              onClick={handleMinimize}
              onMouseDown={(e) => e.stopPropagation()}
              title={isShaded ? 'Expand' : 'Minimize'}
              aria-label={isShaded ? 'Expand' : 'Minimize'}
              aria-expanded={interactive ? !isShaded : undefined}
              type="button"
            >
              <span className={styles.minimizeIcon} />
            </button>

            <button
              className={`${styles.controlBtn} ${styles.maximizeBtn}`}
              onClick={handleMaximize}
              onMouseDown={(e) => e.stopPropagation()}
              title="Maximize"
              aria-label="Maximize"
              type="button"
            >
              <span className={styles.maximizeIcon} />
            </button>

            <button
              className={`${styles.controlBtn} ${styles.closeBtn}`}
              onClick={handleClose}
              onMouseDown={(e) => e.stopPropagation()}
              title="Close"
              aria-label="Close"
              type="button"
            >
              ✕
            </button>
          </div>
        </div>

        {/* ── Content Body ── (grid row animates the window-shade roll-up).
            The clip layer carries no padding so the body rolls fully up to the
            title bar; padding/background live on the inner .body. */}
        <div className={`${styles.bodyWrap} ${isShaded ? styles.bodyWrapShaded : ''}`}>
          <div className={styles.bodyClip}>
            <div className={`${styles.body} ${bodyClassName}`}>
              {inFlowBody}
            </div>
          </div>
        </div>
      </div>

      {/* ── Maximized → full-screen modal ── */}
      {isMaximized && createPortal(
        <div
          className={styles.maxOverlay}
          onMouseDown={exitMaximized}
          role="presentation"
        >
          <div
            className={styles.maxWindow}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            <div className={styles.titleBar}>
              <span className={styles.windowIcon} aria-hidden="true">◈</span>
              <span className={styles.titleText}>{title}</span>
              <div className={styles.controls}>
                <button
                  className={`${styles.controlBtn} ${styles.maximizeBtn}`}
                  onClick={exitMaximized}
                  title="Restore"
                  aria-label="Restore"
                  type="button"
                >
                  <span className={styles.restoreIcon} />
                </button>
                <button
                  className={`${styles.controlBtn} ${styles.closeBtn}`}
                  onClick={exitMaximized}
                  title="Close"
                  aria-label="Close"
                  type="button"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className={`${styles.body} ${styles.maxBody} ${bodyClassName} ${maxBodyClassName}`}>
              {children}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CyberLunaWindow;
